import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Bell, Search, ChevronDown, LogOut, User, Settings } from 'lucide-react';
import Logo from '../common/Logo';
import { useAuth } from '../../hooks/useAuth';

interface NavbarProps {
  onMenuToggle: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onMenuToggle }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  return (
    <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-30">
      <div className="px-4 flex h-16 items-center justify-between">
        <div className="flex items-center">
          <button
            type="button"
            className="text-gray-500 hover:text-gray-600 lg:hidden p-2"
            onClick={onMenuToggle}
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <Link to="/dashboard" className="flex items-center ml-4 lg:ml-0">
            <Logo />
          </Link>
        </div>
        
        <div className="hidden md:block mx-auto max-w-md w-full">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search colleges..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#7B4AE2] focus:border-[#7B4AE2] transition duration-150 ease-in-out text-sm"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <div className="relative">
            <button
              className="p-2 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <span className="sr-only">View notifications</span>
              <Bell className="h-6 w-6" />
              <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"></span>
            </button>
            
            {showNotifications && (
              <div className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg">
                <div className="rounded-md bg-white ring-1 ring-black ring-opacity-5">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
                      <button className="text-xs text-[#7B4AE2]">Mark all as read</button>
                    </div>
                    
                    {/* Notification items */}
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      <div className="flex p-2 rounded-md hover:bg-gray-50">
                        <div className="flex-shrink-0 pt-0.5">
                          <div className="h-10 w-10 rounded-full bg-[#F0E9FF] flex items-center justify-center">
                            <Bell className="h-5 w-5 text-[#7B4AE2]" />
                          </div>
                        </div>
                        <div className="ml-3 w-full">
                          <p className="text-sm font-medium text-gray-900">New college recommendation</p>
                          <p className="text-xs text-gray-500">Based on your profile, we've found a new match.</p>
                          <p className="text-xs text-gray-400 mt-1">2 hours ago</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 text-center">
                      <button className="text-sm text-[#7B4AE2]">View all notifications</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* User menu */}
          <div className="relative ml-3">
            <div>
              <button
                className="flex text-sm rounded-full focus:outline-none"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <span className="sr-only">Open user menu</span>
                <div className="h-8 w-8 rounded-full bg-[#7B4AE2] flex items-center justify-center text-white">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div className="hidden md:flex ml-2 items-center">
                  <span className="text-sm font-medium text-gray-700">{user?.name || 'User'}</span>
                  <ChevronDown className="ml-1 h-4 w-4 text-gray-400" />
                </div>
              </button>
            </div>
            
            {showUserMenu && (
              <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg z-50">
                <div className="py-1 rounded-md bg-white ring-1 ring-black ring-opacity-5">
                  <div className="px-4 py-2 text-xs text-gray-400">
                    Signed in as
                    <p className="text-sm font-medium text-gray-900">{user?.email || 'user@example.com'}</p>
                  </div>
                  
                  <div className="border-t border-gray-100"></div>
                  
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <User className="mr-2 h-4 w-4" />
                    Your Profile
                  </Link>
                  
                  <Link
                    to="/settings"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Link>
                  
                  <div className="border-t border-gray-100"></div>
                  
                  <button
                    onClick={() => {
                      handleLogout();
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;